<script setup lang="ts">
import type { CalendarDraftPeriod, CalendarPeriod } from '@ui/components/calendar/picker'
import { useInventoryPeriodLimits } from '@booking/composables/useInventoryPeriodLimits'
import { formatInventoryDuration } from '@booking/utils/inventoryDuration'
import {
  fromInventoryCalendarDate,
  getNextInventoryStart,
  toInventoryCalendarDate,
} from '@booking/utils/inventoryPeriod'
import { CalendarMode } from '@ui/components/calendar/picker'
import UiCalendar from '@ui/components/calendar/picker/Calendar.vue'
import { getDateInputText } from '@ui/components/input/date/formatInutDate'
import UiTimeSheet from '@ui/components/time-sheet/TimeSheet.vue'
import { ButtonVariant, Sizes } from '@ui/styles'
import { formatDateByLang } from '@ui/utils/times'
import { addDays, addMinutes, setHours, setMinutes } from 'date-fns'
import CalendarIcon from '~icons/cue-regular/calendar'
import Clock from '~icons/cue-regular/clock'

const props = withDefaults(defineProps<{ timezone?: string }>(), { timezone: '' })
const { disabledDate, isValid, getTimeOptions } = useInventoryPeriodLimits(() => props.timezone)

const period = defineModel<CalendarPeriod>({ required: true })
const { locale, t } = useI18n()

const PickerView = {
  Period: 'period',
  StartTime: 'start-time',
  EndTime: 'end-time',
} as const

type PickerView = (typeof PickerView)[keyof typeof PickerView]

const show = ref(false)
const view = ref<PickerView>(PickerView.Period)
const timeView = ref<PickerView>(PickerView.StartTime)
const draftPeriod = ref(clonePeriod(period.value))
const timeEditSnapshot = ref<CalendarPeriod>()
// Keep the calendar's partial date selection independent from editable times.
const calendarSelection = ref<CalendarDraftPeriod>(clonePeriod(period.value))

const periodText = computed(() =>
  getDateInputText(period.value, {
    range: true,
    target: CalendarMode.Times,
    locale: locale.value,
  }).replace(/\./g, ''),
)

const canSavePeriod = computed(() => isValid(draftPeriod.value))
const isTimeView = computed(() => view.value !== PickerView.Period)
const timeSheetShow = computed({
  get: () => show.value && isTimeView.value,
  set: (value: boolean) => {
    if (!value && isTimeView.value) {
      cancelTimeEdit()
    }
  },
})
const editedTimeDate = computed(() =>
  timeView.value === PickerView.StartTime ? draftPeriod.value.start : draftPeriod.value.end,
)
const timeOptions = computed(() =>
  getTimeOptions(editedTimeDate.value, timeView.value === PickerView.EndTime ? draftPeriod.value.start : undefined).map(
    option => option.id,
  ),
)
const selectedTime = computed({
  get: () => getTime(editedTimeDate.value),
  set: selectTime,
})
const timeTitle = computed(() =>
  timeView.value === PickerView.StartTime
    ? t('page.services.inventory.picker.start_time')
    : t('page.services.inventory.picker.end_time'),
)

const duration = computed(() => {
  return formatInventoryDuration(draftPeriod.value, t)
})

const calendarDuration = computed(() => {
  const sourcePeriod = isTimeView.value && timeEditSnapshot.value ? timeEditSnapshot.value : draftPeriod.value

  return formatInventoryDuration(sourcePeriod, t)
})

const durationText = computed(() => t('page.services.inventory.picker.duration', { duration: duration.value }))
const calendarDurationText = computed(() =>
  t('page.services.inventory.picker.duration', { duration: calendarDuration.value }),
)
const startDateLabel = computed(() =>
  t('page.services.inventory.picker.start_date', { date: formatShortDate(draftPeriod.value.start) }),
)
const endDateLabel = computed(() =>
  t('page.services.inventory.picker.end_date', { date: formatShortDate(draftPeriod.value.end) }),
)

watch(show, value => {
  if (!value) {
    view.value = PickerView.Period
  }
})

function clonePeriod(value: CalendarPeriod): CalendarPeriod {
  return {
    start: new Date(value.start),
    end: new Date(value.end),
  }
}

function formatShortDate(date: Date) {
  return formatDateByLang(date, 'd MMM yyyy', locale.value).replace(/\./g, '')
}

function formatTime(minutes: number) {
  const hours = Math.floor(minutes / 60)
  const restMinutes = minutes % 60
  return `${String(hours).padStart(2, '0')}:${String(restMinutes).padStart(2, '0')}`
}

function getTime(date: Date) {
  return date.getHours() * 60 + date.getMinutes()
}

function setDatePart(source: Date, date: Date) {
  const result = new Date(date)
  result.setHours(source.getHours(), source.getMinutes(), 0, 0)
  return result
}

function setTimePart(source: Date, minutes: number) {
  return setMinutes(setHours(source, Math.floor(minutes / 60)), minutes % 60)
}

function openPicker() {
  draftPeriod.value = clonePeriod(period.value)
  if (!isValid(draftPeriod.value, new Date())) {
    draftPeriod.value.start = toInventoryCalendarDate(getNextInventoryStart(props.timezone), props.timezone)
    if (draftPeriod.value.end <= draftPeriod.value.start) {
      draftPeriod.value.end = addDays(draftPeriod.value.start, 1)
    }
  }
  calendarSelection.value = clonePeriod(draftPeriod.value)
  timeEditSnapshot.value = undefined
  view.value = PickerView.Period
  show.value = true
}

function closePicker() {
  show.value = false
}

function savePeriod() {
  if (!canSavePeriod.value || !isValid(draftPeriod.value, new Date())) {
    return
  }

  period.value = clonePeriod(draftPeriod.value)
  closePicker()
}

function onSelectDate(value: Date | CalendarDraftPeriod) {
  if (value instanceof Date || !value.start || disabledDate(value.start) || (value.end && disabledDate(value.end))) {
    return
  }

  let start = setDatePart(draftPeriod.value.start, value.start)
  if (fromInventoryCalendarDate(start, props.timezone) < new Date()) {
    start = toInventoryCalendarDate(getNextInventoryStart(props.timezone), props.timezone)
  }
  // One date is a valid same-day rental; a second date extends the range.
  let end = setDatePart(draftPeriod.value.end, value.end || value.start)
  if (end <= start) {
    end = addMinutes(start, 15)
  }

  calendarSelection.value = {
    start: new Date(start),
    end: value.end ? new Date(end) : undefined,
  }
  draftPeriod.value = { start, end }
}

function syncCompleteCalendarSelection() {
  if (calendarSelection.value.end) {
    calendarSelection.value = clonePeriod(draftPeriod.value)
  }
}

function openTimePicker(nextView: typeof PickerView.StartTime | typeof PickerView.EndTime) {
  timeEditSnapshot.value = clonePeriod(draftPeriod.value)
  timeView.value = nextView
  view.value = nextView
  if (!timeOptions.value.includes(selectedTime.value) && timeOptions.value.length) {
    selectTime(timeOptions.value[0]!)
  }
}

function selectTime(minutes: number) {
  if (!timeOptions.value.includes(minutes)) {
    return
  }
  if (timeView.value === PickerView.StartTime) {
    const start = setTimePart(draftPeriod.value.start, minutes)
    draftPeriod.value = {
      start,
      end: start >= draftPeriod.value.end ? addDays(start, 1) : draftPeriod.value.end,
    }
    syncCompleteCalendarSelection()
    return
  }

  let end = setTimePart(draftPeriod.value.end, minutes)
  if (end <= draftPeriod.value.start) {
    end = addDays(end, 1)
  }
  draftPeriod.value = { start: draftPeriod.value.start, end }
  syncCompleteCalendarSelection()
}

function cancelTimeEdit() {
  if (timeEditSnapshot.value) {
    draftPeriod.value = clonePeriod(timeEditSnapshot.value)
    syncCompleteCalendarSelection()
  }
  view.value = PickerView.Period
}

function saveTimeEdit() {
  if (!isValid(draftPeriod.value, new Date())) {
    return
  }
  timeEditSnapshot.value = undefined
  view.value = PickerView.Period
}
</script>

<template lang="pug">
UiInput.period-input(
  readonly
  :background="Background.White"
  :modelValue="periodText"
  :iconRight="CalendarIcon"
  :size="Sizes.Leia"
  @focus="openPicker"
)

UiBottomSheet(
  v-model:show="show"
  label="page.services.inventory.period"
  headerAlign="center"
  :description="calendarDurationText"
  :closable="false"
  :snapPoints="[680]"
)
  .inventory-period-sheet.flex.col-0.overflow-hidden(
    @click.stop
  )
    .sheet-content.flex.col-0.flex-1.overflow-auto
      UiCalendar.calendar.flex-shrink-0(
        :modelValue="calendarSelection"
        :range="true"
        :disabledDate="disabledDate"
        :target="CalendarMode.Days"
        @select:date="onSelectDate"
      )

      p.time-label.typ-12-18-400.m-0.m-t-12 {{ $t('page.services.inventory.picker.period_hint') }}

      .time-fields.flex.row-16.p-t-16
        button.time-field.flex.col-4.flex-1.min-w-0.p-0.border-none.text-left.bg-transparent.cursor-pointer(
          type="button"
          @click="openTimePicker(PickerView.StartTime)"
        )
          span.time-label.typ-12-18-500.ellipsis {{ startDateLabel }}
          span.time-input.flex-between-center.typ-16-24-400.h-48.p-12-16.br-16
            span {{ formatTime(getTime(draftPeriod.start)) }}
            UiIcon.time-icon.s-20.flex-shrink-0(:name="Clock")
        button.time-field.flex.col-4.flex-1.min-w-0.p-0.border-none.text-left.bg-transparent.cursor-pointer(
          type="button"
          @click="openTimePicker(PickerView.EndTime)"
        )
          span.time-label.typ-12-18-500.ellipsis {{ endDateLabel }}
          span.time-input.flex-between-center.typ-16-24-400.h-48.p-12-16.br-16
            span {{ formatTime(getTime(draftPeriod.end)) }}
            UiIcon.time-icon.s-20.flex-shrink-0(:name="Clock")

  template(#actions)
    UiButton.flex-1(
      label="page.services.inventory.picker.cancel"
      :variant="ButtonVariant.Secondary"
      :size="Sizes.HanSolo"
      @click="closePicker"
    )
    UiButton.flex-1(
      label="page.services.inventory.picker.save"
      :variant="ButtonVariant.Primary"
      :size="Sizes.HanSolo"
      :disabled="!canSavePeriod"
      @click="savePeriod"
    )

UiTimeSheet(
  v-model:show="timeSheetShow"
  v-model="selectedTime"
  cancelLabel="page.services.inventory.picker.cancel"
  saveLabel="page.services.inventory.picker.save"
  :label="timeTitle"
  :options="timeOptions"
  :loop="false"
  :saveDisabled="!isValid(draftPeriod)"
  :description="durationText"
  @cancel="cancelTimeEdit"
  @save="saveTimeEdit"
)
</template>

<style scoped src="./InventoryPeriodPicker.css"></style>
